import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from "@angular/core";
import { EngagementService } from "../engagement-metrics/engagement.service";
import { s25Resource } from "../../../util/s25.resource";
import { TrailApi, type RequirementDomain, type RequirementRootSummary } from "./trail.api";
import {
    TrailRequirementTreeComponent,
    wireTreeToEditorNode,
    type IncludeTreeCohortOption,
    type RequirementCourseOption,
    type RequirementNode,
} from "./trail.requirement.tree.component";

export function includeTreeCohortsFrom(
    cohortIds: number[],
    cohorts: { cohortId?: number; cohortCode: string }[],
    roots: RequirementRootSummary[],
    excludeRootGroupId?: number | null,
): IncludeTreeCohortOption[] {
    const rootsByCohortId = new Map<number, RequirementRootSummary[]>();
    for (const root of roots) {
        if (root.cohortId == null) continue;
        if (excludeRootGroupId != null && root.requirementGroupId === excludeRootGroupId) continue;
        const cohortRoots = rootsByCohortId.get(root.cohortId);
        if (cohortRoots) {
            cohortRoots.push(root);
        } else {
            rootsByCohortId.set(root.cohortId, [root]);
        }
    }
    return cohortIds.map((cohortId) => {
        const cohort = cohorts.find((item) => item.cohortId === cohortId);
        return {
            cohortId,
            cohortCode: cohort?.cohortCode ?? "",
            roots: (rootsByCohortId.get(cohortId) ?? []).map((root) => ({
                rootGroupId: root.requirementGroupId,
                name: root.name,
                code: root.code,
            })),
        };
    });
}

@Component({
    selector: "s25-ng-trail-requirement-tree-editor",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TrailRequirementTreeComponent],
    host: { class: "trail-requirement-tree-editor" },
    template: `
        @if (isLoading()) {
            <p>Loading tree…</p>
        } @else if (loadError()) {
            <p class="c-text--error">{{ loadError() }}</p>
        } @else if (tree()) {
            @if (showStatus()) {
                <p class="c-text--secondary c-margin-bottom--half">{{ isPublished() ? "Published" : "Draft" }}</p>
            }
            <s25-ng-trail-requirement-tree
                [node]="tree()!"
                [depth]="0"
                [editable]="editable()"
                [domain]="domain()"
                [badges]="badgeOptions()"
                [trails]="trailOptions()"
                [courses]="courses()"
                [includeTreeCohorts]="includeTreeCohorts()"
                (nodeChange)="onTreeChange($event)"
            />
        }
    `,
})
export class TrailRequirementTreeEditorComponent {
    readonly cohortId = input<number | null>(null);
    readonly cohortIds = input<number[]>([]);
    readonly rootGroupId = input<number | null>(null);
    readonly node = input<RequirementNode | null>(null);
    readonly editable = input(true);
    readonly domain = input<RequirementDomain>("COCURRICULAR");
    readonly courses = input<RequirementCourseOption[]>([]);
    readonly showStatus = input(false);
    readonly includeTrails = input(true);

    readonly nodeChange = output<RequirementNode>();

    readonly cohortIdList = computed((): number[] => {
        const single = this.cohortId();
        if (single != null && Number.isFinite(single)) {
            return [single];
        }
        return this.cohortIds().filter((id) => Number.isFinite(id));
    });

    readonly excludeRootGroupId = computed((): number | null => this.rootGroupId());

    readonly shouldLoadSupport = computed(
        () => this.cohortIdList().length > 0 && (this.node() != null || this.rootGroupId() != null),
    );

    readonly treeDetailResource = s25Resource({
        if: () => this.rootGroupId() != null && this.node() == null,
        params: () => this.rootGroupId(),
        loader: ({ params }) => TrailApi.getRequirementRoot(params!),
    });

    readonly badgesResource = s25Resource({
        if: () => this.shouldLoadSupport(),
        loader: () => EngagementService.getBadges(),
    });

    readonly trailsResource = s25Resource({
        if: () => this.shouldLoadSupport() && this.includeTrails() && this.cohortIdList().length === 1,
        params: () => this.cohortIdList()[0],
        loader: ({ params }) => TrailApi.getCohortTrails(params),
    });

    readonly cohortsResource = s25Resource({
        if: () => this.shouldLoadSupport(),
        params: () => (this.cohortIdList().length === 1 ? this.cohortIdList()[0] : null),
        loader: ({ params }) => (params != null ? TrailApi.getCohorts(params) : TrailApi.getCohorts()),
    });

    readonly generalRootsResource = s25Resource({
        if: () => this.shouldLoadSupport(),
        params: () => this.cohortIdList(),
        loader: ({ params }) => TrailApi.listRequirementRoots(params, "GENERAL"),
    });

    readonly tree = linkedSignal((): RequirementNode | null => {
        const provided = this.node();
        if (provided) {
            return provided;
        }
        const detail = this.treeDetailResource.value();
        if (!detail) {
            return null;
        }
        return wireTreeToEditorNode(detail.requirementTree, this.cohortIdList()[0]);
    });

    readonly badgeOptions = computed((): { badgeId: number; name: string }[] => {
        return (this.badgesResource.value() ?? [])
            .filter((badge) => badge.badgeId != null)
            .map((badge) => ({ badgeId: badge.badgeId!, name: badge.name }));
    });

    readonly trailOptions = computed((): { trailId: number; name: string }[] => {
        return (this.trailsResource.value() ?? [])
            .filter((trail) => trail.trailId != null && !trail.isDisabled)
            .map((trail) => ({ trailId: trail.trailId!, name: trail.name }));
    });

    readonly includeTreeCohorts = computed((): IncludeTreeCohortOption[] => {
        return includeTreeCohortsFrom(
            this.cohortIdList(),
            this.cohortsResource.value() ?? [],
            this.generalRootsResource.value() ?? [],
            this.excludeRootGroupId(),
        );
    });

    readonly isPublished = computed(() => this.treeDetailResource.value()?.isPublished ?? false);

    readonly isLoading = computed(
        () => this.rootGroupId() != null && this.node() == null && this.treeDetailResource.resource.isLoading(),
    );

    readonly loadError = computed((): string | null => {
        const error = this.treeDetailResource.resource.error();
        if (!error) {
            return null;
        }
        return error instanceof Error ? error.message : String(error);
    });

    onTreeChange(updated: RequirementNode) {
        this.tree.set(updated);
        this.nodeChange.emit(updated);
    }
}
